import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config";
import { Mail, Lock, UserPlus, Gift, Eye, EyeOff, LogIn } from "lucide-react";

export const RegisterForm = ({ onSwitchToLogin, embedded = false, onVerificationReady }) => {
  const { authError, setAuthError } = useAuth();
  const location = useLocation();

  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [referralCodeInput, setReferralCodeInput] = useState("");
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const abortRef = useRef(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get("ref");
    if (refCode) setReferralCodeInput(refCode);
  }, [location.search]);

  useEffect(() => {
    setAuthError(null);
    return () => abortRef.current?.abort();
  }, [setAuthError]);

  const canSubmit = useMemo(
    () =>
      !loadingRegister &&
      emailInput.trim().length > 0 &&
      passwordInput.length >= 8,
    [loadingRegister, emailInput, passwordInput]
  );

  const parseErrors = async (response) => {
    const ct = (response.headers.get("content-type") || "").toLowerCase();
    const bodyText = await response.text();

    const looksJson = ct.includes("application/json") || /^[\[{]/.test(bodyText.trim());
    if (looksJson) {
      let data = null;
      try {
        data = bodyText ? JSON.parse(bodyText) : null;
      } catch {}
      const pick = (field) => {
        const v = data && data[field];
        if (Array.isArray(v)) return v[0];
        if (typeof v === "string") return v;
        return null;
      };
      return (
        pick("username") ||
        pick("email") ||
        pick("password") ||
        pick("password2") ||
        pick("non_field_errors") ||
        (data && data.detail) ||
        `Registration failed (HTTP ${response.status}).`
      );
    }

    const title = (bodyText.match(/<title>([^<]+)<\/title>/i) || [])[1];
    const pre = (bodyText.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i) || [])[1];
    const snippet = (pre || "").split("\n").slice(0, 2).join(" ").trim();
    return title || snippet || `Server error (HTTP ${response.status}).`;
  };

  const handleRegisterSubmit = async (event) => {
    event.preventDefault();
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoadingRegister(true);
    setAuthError(null);

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const payload = {
        username: emailInput.trim(),
        email: emailInput.trim(),
        password: passwordInput,
        password2: passwordInput,
        referral_code: referralCodeInput.trim() || "",
      };
      const response = await fetch(`${API_BASE_URL}/api/register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!response.ok) {
        const msg = await parseErrors(response);
        throw new Error(msg);
      }

      const nextEmail = emailInput.trim();
      setEmailInput("");
      setPasswordInput("");
      setReferralCodeInput("");
      onVerificationReady?.(nextEmail);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Registration failed:", err);
        setAuthError(err.message || "Registration failed. Please try again.");
      }
    } finally {
      setLoadingRegister(false);
      inFlightRef.current = false;
    }
  };

  const fieldWrapperClasses =
    "flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/85 px-4 py-3 text-sm text-slate-900 shadow-inner shadow-white/40 transition focus-within:border-slate-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-slate-400";

  const inputClasses =
    "flex-1 border-0 bg-transparent p-0 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0";

  const formFields = (
    <div className="flex flex-col gap-3">
      <label className="text-sm font-medium text-slate-600">
        <span className="sr-only">Email</span>
        <div className={fieldWrapperClasses}>
          <Mail size={20} className="text-slate-400" />
          <input
            type="email"
            autoComplete="email"
            required
            className={inputClasses}
            placeholder="name@company.com"
            value={emailInput}
            onChange={(event) => setEmailInput(event.target.value)}
          />
        </div>
      </label>
      <label className="text-sm font-medium text-slate-600">
        <span className="sr-only">Password</span>
        <div className={`${fieldWrapperClasses} pr-2`}>
          <Lock size={20} className="text-slate-400" />
          <input
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            required
            minLength={8}
            className={inputClasses}
            placeholder="Minimum 8 characters"
            value={passwordInput}
            onChange={(event) => setPasswordInput(event.target.value)}
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="inline-flex items-center justify-center text-slate-500 transition hover:text-slate-700 focus:outline-none"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
      </label>
      <label className="mt-4 text-sm font-medium text-slate-600 md:col-span-2">
        <span className="sr-only">Referral code (optional)</span>
        <div className={fieldWrapperClasses}>
          <Gift size={20} className="text-slate-400" />
          <input
            type="text"
            className={inputClasses}
            placeholder="Referral code (optional)"
            value={referralCodeInput}
            onChange={(event) => setReferralCodeInput(event.target.value)}
          />
        </div>
      </label>
    </div>
  );

  const formContent = (
    <div className="flex flex-col gap-3">
      <div className="space-y-3 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
          <UserPlus size={28} />
        </div>
        <div className="space-y-1">
          <h2 className="text-3xl font-semibold text-slate-900">Create an account</h2>
          <p className="text-sm text-slate-500">Enter your details to get started.</p>
        </div>
      </div>

      <form className="grid gap-6" onSubmit={handleRegisterSubmit} noValidate>
        <fieldset disabled={loadingRegister} className="grid gap-4">
          {formFields}
        </fieldset>

        {authError && (
          <div className="rounded-2xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-600" role="alert" aria-live="assertive">
            <strong className="font-semibold">Registration error:</strong>
            <span className="ml-2">{authError}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-slate-900 to-slate-950 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 transition-colors duration-200 transition-transform hover:-translate-y-1 hover:scale-105 hover:from-slate-800 hover:to-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loadingRegister ? "Registering..." : "Create account"}
        </button>
      </form>

      <div className="text-center text-sm text-slate-500">
        Already have an account?{" "}
        <button
          type="button"
          onClick={() => onSwitchToLogin?.()}
          className="inline-flex items-center justify-center gap-1 font-semibold text-slate-900 underline-offset-4 transition decoration-slate-900 transition hover:text-slate-700 hover:underline hover:decoration-slate-700"
        >
          <LogIn size={16} /> Log in
        </button>
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className="max-h-[calc(100vh-140px)] overflow-y-auto px-6 py-6 sm:px-8 sm:py-8">
        {formContent}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="flex min-h-screen items-center justify-center px-4 py-8 md:px-6 md:py-10">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/95 p-10 text-slate-900 shadow-[0_35px_120px_-45px_rgba(15,23,42,0.8)] backdrop-blur-2xl">
          {formContent}
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
